import {
    createContext,
    useReducer,
    useContext,
    useEffect,
    useCallback,
    useRef,
} from 'react';
import { login as masterLogin } from '../services/Master';
import { login as studentLogin } from '../services/Student';
import { languageCodes, browserStoragePrefix } from '../services/config';
import { useTranslation } from 'react-i18next';

export const AuthContext = createContext();

// https://www.digigreenhouse.wur.nl/edu/Game/#/master?key=29b21a76-80c7-4716-bde0-df0a4aaa1f92

/**
 * permissionLevel
 *  0 - none
 *  1 - student
 *  2 - master
 *  3 - superadmin
 */
const initialState = {
    ready: false,
    fetching: false,
    isAuthenticated: false,
    username: null,
    token: null,
    useremail: null,
    permissionLevel: 0,
    game: null,
    gameConfig: null,
    /**
     * gameStatus.status:
     *  init, sim, ready, score?
     */
    gameStatus: null,
    gameForecast: null,
    gameSession: null,
    languageId: 0,
    helpUrl: null,
    /* (@)replay :
        0: herstart button verbergen
        1: herstart button na uitspelen tonen
        2: herstart button altijd tonen*/
    replay: 0,
};

const getSessionValue = (key) =>
    sessionStorage.getItem(`${browserStoragePrefix}${key}`);
const setSessionValue = (key, val) =>
    sessionStorage.setItem(`${browserStoragePrefix}${key}`, val);

const getSessionStorageState = () => ({
    ...initialState,
    username: JSON.parse(getSessionValue('username') || initialState.username),
    token: JSON.parse(getSessionValue('token') || initialState.token),
    useremail: JSON.parse(
        getSessionValue('useremail') || initialState.useremail
    ),
});

const reducer = (state, action) => {
    switch (action.type) {
        case 'LOGIN': {
            const { permissionLevel, token } = action.payload;
            const master = permissionLevel === 2 || permissionLevel === '2';
            const name = master
                ? action.payload.name
                : action.payload.status.username;
            const email = master
                ? action.payload.email
                : action.payload.status.useremail;
            let userGameParameters = {};
            let masterSettings = {};
            if (master) {
                masterSettings.configs = action.payload.configs;
            } else {
                userGameParameters = {
                    game: action.payload.game,
                    gameConfig: action.payload.config,
                    gameStatus: action.payload.status,
                    gameForecast: action.payload.forecast,
                    gameSession: action.payload.session,
                    replay: +action.payload.replay,
                };
                if (action.payload.config?.['@help']?.user?.default) {
                    const wikiurl = action.payload.config?.['@help'].wikiurl;
                    userGameParameters.helpUrl = `${wikiurl}${action.payload.config['@help'].user.default}`;
                }
            }
            setSessionValue('username', JSON.stringify(name || ''));
            setSessionValue('useremail', JSON.stringify(email || ''));
            setSessionValue('token', JSON.stringify(token || ''));
            setSessionValue(
                'permissionlevel',
                JSON.stringify(permissionLevel || 0)
            );

            return {
                ...state,
                isAuthenticated: true,
                ready: true,
                fetching: false,
                username: name,
                useremail: email,
                token,
                permissionLevel,
                ...userGameParameters,
                ...masterSettings,
            };
        }
        case 'LOGOUT':
            sessionStorage.clear();
            return { ...initialState, username: '', token: '', ready: true };

        case 'FAILED_LOGIN': {
            const token = action.payload;
            setSessionValue('username', '');
            setSessionValue('useremail', '');
            setSessionValue('permissionlevel', '');
            return { ...initialState, token, ready: true, fetching: false };
        }
        case 'SET_HELP_URL':
            return { ...state, helpUrl: action.payload };

        case 'UPDATE_GAME_DATA': {
            const { histdata, lastdata, status, forecast } = action.payload;
            let updatedState = {
                gameStatus: status,
                gameSession: { ...state.gameSession, histdata, lastdata },
            };
            if (forecast) {
                updatedState.gameForecast = forecast;
            }
            return {
                ...state,
                ...updatedState,
            };
        }

        case 'NO_KEY':
            return { ...state, ready: true, fetching: false };

        case 'START_FETCH':
            return { ...state, fetching: true };
        case 'CHANGE_LANGUAGE':
            return { ...state, languageId: action.payload };
        default:
            return state;
    }
};

// Action creators
export const setUser = (userData) => ({
    type: 'LOGIN',
    payload: userData,
});
export const logout = () => ({ type: 'LOGOUT' });
/**
 *
 * @param {Object} payload
 * @param {Object} payload.histdata
 * @param {Object} payload.lastdata
 * @param {Object} payload.status
 * @returns
 */
export const updateGameData = (payload) => ({
    type: 'UPDATE_GAME_DATA',
    payload,
});
export const changeLanguage = (languageId = 0) => ({
    type: 'CHANGE_LANGUAGE',
    payload: languageId,
});
export const setHelpUrl = (helpUrl) => ({
    type: 'SET_HELP_URL',
    payload: helpUrl,
});

const AuthProvider = (props) => {
    const [auth, dispatch] = useReducer(reducer, getSessionStorageState());
    const { i18n } = useTranslation();

    const isMounted = useRef(true);
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const doLogin = useCallback((token, pLevel) => {
        if (token) {
            dispatch({ type: 'START_FETCH' });
            const loginPromise =
                pLevel === 2 || pLevel === '2'
                    ? masterLogin(token)
                    : studentLogin(token);
            loginPromise
                .then((userData) => {
                    if (isMounted.current) {
                        dispatch(setUser({ ...userData, token }));
                    }
                })
                .catch((error) => {
                    if (error) {
                        console.warn(error);
                    }
                    if (isMounted.current) {
                        dispatch({ type: 'FAILED_LOGIN', payload: token });
                    }
                });
        } else {
            dispatch({ type: 'NO_KEY' });
        }
    }, []);

    useEffect(() => {
        const token = JSON.parse(getSessionValue('token') || null);
        const permissionLevel = JSON.parse(
            getSessionValue('permissionlevel') || null
        );
        doLogin(token, permissionLevel);
    }, [doLogin]);

    useEffect(() => {
        if (i18n && !(typeof auth?.game?.language === 'undefined')) {
            dispatch(changeLanguage(auth.game.language));
        }
    }, [auth?.game?.language, i18n]);

    useEffect(() => {
        if (i18n && !(typeof auth?.languageId === 'undefined')) {
            i18n.changeLanguage(languageCodes[auth?.languageId]);
        }
    }, [auth?.languageId, i18n]);

    /**
     * Triggers a reload of login/user-data in the background
     */
    const reload = () => {
        const { token, permissionLevel } = auth;
        doLogin(token, permissionLevel);
    };
    return (
        <AuthContext.Provider
            value={{
                auth,
                dispatch,
                reload,
                changeLanguage,
            }}
            {...props}
        />
    );
};

const useAuthContext = () => useContext(AuthContext);

export { AuthProvider, useAuthContext };
